import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import { DIFF_NUMBER_DOT_COMMA_REGEX } from "@/constants/regex";
import {
  formatDecimalNumber,
  numberIsGreaterThan,
  sanitizeDecimalInput
} from "@/helpers/numberHelpers";

import { MinMaxRange } from "./types";

type MinMaxFilterProps = {
  type?: string;
  onSave: (data: MinMaxRange) => void;
  defaultValue?: MinMaxRange;
};

const MinMaxFilter = ({ type, onSave, defaultValue }: MinMaxFilterProps) => {
  const form = useForm<MinMaxRange>({
    defaultValues: {
      min: defaultValue?.min || "",
      max: defaultValue?.max || "",
      includeNulls: defaultValue?.includeNulls || false
    },
    mode: "onChange"
  });
  const {
    register,
    watch,
    formState: { errors },
    setValue,
    trigger,
    clearErrors
  } = form;
  const minInputRef = useRef<HTMLInputElement>(null);
  const maxInputRef = useRef<HTMLInputElement>(null);

  // Watch form values to trigger validation and updates
  const formValues = watch();

  const validateMin = (value: string) => {
    if (!value || !formValues.max || formValues.max === "") return true;
    if (numberIsGreaterThan(value, formValues.max, false)) {
      return "Value needs to be smaller than or equal to maximum";
    }
    return true;
  };

  const validateMax = (value: string) => {
    if (!value || !formValues.min || formValues.min === "") return true;

    if (!numberIsGreaterThan(value, formValues.min)) {
      return "Value needs to be larger than or equal to minimum";
    }

    return true;
  };

  const handleKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;
    const currentValue = e.currentTarget.value;
    // Not allow enter dot when type is int
    if (type === "int" && key === ".") {
      e.preventDefault();
    }
    if (
      DIFF_NUMBER_DOT_COMMA_REGEX.test(key) ||
      (currentValue.endsWith(",") && [",", "."].includes(key))
    ) {
      e.preventDefault();
    }
  };

  // Shared onChange handler for both min and max fields
  const handleInputChange =
    (fieldName: "min" | "max") => (e: React.ChangeEvent<HTMLInputElement>) => {
      // Clear any existing errors first
      clearErrors();
      const rawValue = e.target.value;
      setValue(fieldName, sanitizeDecimalInput(rawValue));
      trigger(fieldName);
      onSave({ ...formValues, [fieldName]: rawValue });
    };

  // Format the input values on blur
  const handleBlur = (fieldName: "min" | "max") => () => {
    const currentValue = watch(fieldName);
    if (currentValue) {
      setValue(fieldName, formatDecimalNumber(currentValue));
      onSave({ ...formValues, [fieldName]: currentValue });
    }
  };

  useEffect(() => {
    if (!defaultValue) {
      form.reset({
        min: "",
        max: "",
        includeNulls: false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  return (
    <div className="flex flex-col flex-1 min-h-0 mt-3">
      <div className="space-y-4">
        {/* Minimum input */}
        <div className="space-y-2">
          <span className="text-sm font-bold">Minimum</span>
          <input
            placeholder="Enter minimum value"
            className={`input-no-arrow w-full h-[40px] px-3 border rounded-md text-sm ${
              errors.min ? "border-red-500" : "border-gainsboro"
            }`}
            value={formValues.min}
            onKeyDown={handleKeydown}
            {...register("min", {
              validate: validateMin
            })}
            ref={minInputRef}
            onChange={handleInputChange("min")}
            onBlur={handleBlur("min")}
          />
          {errors.min && (
            <span className="text-xs text-red-500">{errors.min.message}</span>
          )}
        </div>

        {/* Maximum input */}
        <div className="space-y-2">
          <span className="text-sm font-bold">Maximum</span>
          <input
            placeholder="Enter maximum value"
            className={`input-no-arrow w-full h-[40px] px-3 border rounded-md text-sm ${
              errors.max ? "border-red-500" : "border-gainsboro"
            }`}
            value={formValues.max}
            onKeyDown={handleKeydown}
            {...register("max", {
              validate: validateMax
            })}
            ref={maxInputRef}
            onChange={handleInputChange("max")}
            onBlur={handleBlur("max")}
          />
          {errors.max && (
            <span className="text-xs text-red-500">{errors.max.message}</span>
          )}
        </div>

        {/* Include Nulls checkbox */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="rounded border-gray-300 w-3.5 h-3.5 accent-primary-teal"
            {...register("includeNulls")}
            onChange={(e) => {
              onSave({ ...formValues, includeNulls: e.target.checked });
            }}
          />
          <span className="text-xs text-txtBody font-semibold">
            Include Nulls
          </span>
        </label>
      </div>
    </div>
  );
};

export default MinMaxFilter;
